import { useMemo } from "react";
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  type EntityListOfTransactionItem,
  getTransactions,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import type { TransactionFilters } from "Components/Transactions/hooks/useTransactionFilterManager";
import {
  filtersToQueryParams,
  type TransactionFilterParams,
} from "Components/Transactions/utils/transactionFilterUtils";

const FILTERED_TRANSACTIONS_QUERY_KEY = "FILTERED_TRANSACTIONS";

export const filteredTransactionsKeys = {
  all: [FILTERED_TRANSACTIONS_QUERY_KEY],
  byFilters: (params: TransactionFilterParams) => [
    ...filteredTransactionsKeys.all,
    params,
  ],
};

type Props = {
  filters: TransactionFilters;
  limit?: number;
  nextPageLimit?: number;
  enabled?: boolean;
};

type PageParams = {
  offset: number;
  limit: number;
};

export const useFilteredTransactions = ({
  filters,
  limit = 10,
  nextPageLimit = 25,
  enabled = true,
}: Props) => {
  const queryParams = useMemo(() => filtersToQueryParams(filters), [filters]);

  return useInfiniteQuery<
    EntityListOfTransactionItem,
    unknown,
    InfiniteData<EntityListOfTransactionItem>,
    QueryKey,
    PageParams
  >({
    enabled,
    queryKey: filteredTransactionsKeys.byFilters(queryParams),
    queryFn: processResponseWithApiProblemDetails(
      ({ pageParam: { limit, offset } }) => {
        return getTransactions(
          offset,
          limit,
          undefined, // searchPublicID is now handled separately by useSearchTransactions
          queryParams.status,
          queryParams.dateFrom,
          queryParams.dateTo,
          queryParams.payPointPublicID,
          queryParams.employeePublicID,
          queryParams.direction,
          queryParams.isRefundOrCancellation,
        );
      },
    ),
    initialPageParam: { offset: 0, limit },
    getNextPageParam: (lastPage: EntityListOfTransactionItem | null) => {
      if (!lastPage) {
        return undefined;
      }

      const isFinalPage =
        lastPage.offset + lastPage.limit >= lastPage.totalCount;

      if (isFinalPage) {
        return undefined;
      }

      return { offset: lastPage.offset + lastPage.limit, limit: nextPageLimit };
    },
  });
};
